import { Link } from "@tanstack/react-router";
import { ExternalLink, Package } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { EntityLink } from "@/components/shared/EntityLink";
import RelatedDataTabs from "@/components/shared/RelatedDataTabs";
import { EntityAuditFooter } from "@/components/shared/EntityAuditFooter";
import { formatDate, formatNumber } from "@/lib/utils";

interface Props {
  item: any | null;
  statusLabel?: string;
  onOpenChange: (open: boolean) => void;
}

/**
 * Chi tiết 1 dòng nhu cầu vật tư (trang Kế hoạch vật tư): thông tin vật tư, nhu cầu / tồn / thiếu,
 * nhà cung cấp và các lệnh sản xuất đang cần vật tư này.
 */
export function MaterialPlanRowDetailSheet({ item, statusLabel, onOpenChange }: Props) {
  const materialId = item?.plannedMaterialId || item?.plannedMaterial?.id || item?.materialId || null;
  const code = item?.materialCode || item?.plannedMaterial?.code || "";
  const name = item?.materialName || item?.plannedMaterial?.name || item?.name || "";
  const needed = Number(item?.neededQty || item?.requiredQty || 0);
  const stock = Number(item?.stockQty || 0);
  const shortage = Number(item?.shortageQty || 0);
  const unit = item?.unit || "";
  const orders: string[] = (item?.relatedPo || []).map((po: any) => String(typeof po === "object" ? po.orderId || po.id : po));
  // id dòng nhu cầu (dòng tách lấy materialPlanId) cho thảo luận / đính kèm / lịch sử / người tạo
  const planId: string | null = item?.materialPlanId || item?.id || null;

  return (
    <Sheet open={!!item} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="material-plan-detail">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Package className="h-4 w-4 text-indigo-600" />
            Chi tiết nhu cầu vật tư
          </SheetTitle>
          <SheetDescription>{statusLabel ? `Trạng thái: ${statusLabel}` : ""}</SheetDescription>
        </SheetHeader>

        {item && (
          <div className="space-y-4 px-4 pb-6 text-sm">
            <section className="space-y-1">
              {materialId ? (
                <div className="font-semibold leading-snug break-words [&_a]:whitespace-normal [&_a]:break-words">
                  <EntityLink entityType="material" id={materialId} code={code || undefined} label={name} />
                </div>
              ) : (
                <div className="font-semibold">{code ? `${code} · ` : ""}{name}</div>
              )}
              <div className="text-xs text-slate-500">
                {[item.materialType?.label || item.category, item.suppliedSize || item.specs].filter(Boolean).join(" · ")}
              </div>
            </section>

            <section className="grid grid-cols-3 gap-2" data-testid="material-plan-detail-qty">
              <div className="rounded-lg border border-slate-200 p-2">
                <div className="text-[11px] text-slate-500">Cần dùng</div>
                <div className="font-semibold tabular-nums">{formatNumber(needed)} <span className="text-[11px] font-normal text-slate-500">{unit}</span></div>
              </div>
              <div className="rounded-lg border border-slate-200 p-2">
                <div className="text-[11px] text-slate-500">Tồn kho</div>
                <div className="font-semibold tabular-nums text-emerald-700">{formatNumber(stock)} <span className="text-[11px] font-normal text-slate-500">{unit}</span></div>
              </div>
              <div className="rounded-lg border border-slate-200 p-2">
                <div className="text-[11px] text-slate-500">Còn thiếu</div>
                <div className={shortage > 0 ? "font-semibold tabular-nums text-rose-700" : "font-semibold tabular-nums text-slate-700"}>
                  {formatNumber(shortage)} <span className="text-[11px] font-normal text-slate-500">{unit}</span>
                </div>
              </div>
            </section>
            {needed > 0 && (
              <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden" title="Tồn kho đáp ứng">
                <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, Math.round((stock / needed) * 100))}%` }} />
              </div>
            )}

            <section className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs font-semibold text-slate-600 mb-1">Nhà cung cấp</div>
                <div className="break-words" data-testid="material-plan-detail-vendor">
                  {item.vendorId ? (
                    <EntityLink entityType="vendor" id={item.vendorId} code={item.vendorCode} label={item.vendorName} />
                  ) : item.vendorName ? (
                    item.vendorName
                  ) : (
                    <span className="text-slate-400">Chưa gán NCC</span>
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-600 mb-1">Ngày cần hàng</div>
                <div className="tabular-nums">{item.expectedDate ? formatDate(item.expectedDate) : <span className="text-slate-400">Chưa đặt</span>}</div>
              </div>
            </section>

            <section>
              <div className="text-xs font-semibold text-slate-600 mb-1.5">Lệnh sản xuất cần dùng ({orders.length})</div>
              {orders.length === 0 ? (
                <p className="text-xs text-slate-500">Không có lệnh sản xuất liên kết.</p>
              ) : (
                <ul className="space-y-1" data-testid="material-plan-detail-orders">
                  {orders.map((code) => (
                    <li key={code}>
                      <Link
                        to="/production-orders/$orderId"
                        params={{ orderId: code }}
                        search={{ tab: "materials" } as any}
                        className="inline-flex items-center gap-1.5 text-indigo-700 hover:underline"
                      >
                        <Badge variant="outline" className="font-mono text-[11px] border-indigo-200 bg-indigo-50/60">{code}</Badge>
                        <span className="text-xs">Xem kế hoạch vật tư của lệnh</span>
                        <ExternalLink className="h-3 w-3" />
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {planId && (
              <>
                <RelatedDataTabs entityType="material_plan" entityId={planId} className="mt-2" />
                <EntityAuditFooter entityType="material_plan" entityId={planId} fallback={{ created: item.createdAt, updated: item.updatedAt }} />
              </>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
